import CommonApi from '@/pages/api'
import dayjs, { Dayjs } from 'dayjs'
import utc from 'dayjs/plugin/utc'
import customParseFormat from 'dayjs/plugin/customParseFormat'
dayjs.extend(utc)
dayjs.extend(customParseFormat)
interface DuplicateItem {
  [key: string | number]: any
}

// AI context ref (Responses API migration): prefer conversation_id, fall back to
// legacy thread_id. The id is opaque — never parse or compare prefixes.
export const getAiRef = (o: any = {}) => o?.conversation_id || o?.thread_id || null

export const getUpdatedValues = (oldObject: any, newObject: any) => {
  const changedValues: any = {}

  // Iterate over keys in the new object
  for (const key in newObject) {
    if (oldObject && oldObject.hasOwnProperty(key)) {
      // Check if the value has changed (using deep comparison)
      if (!Object.is(newObject[key], oldObject[key])) {
        changedValues[key] = newObject[key]
      }
    } else {
      // New key, consider it a change
      changedValues[key] = newObject[key]
    }
  }

  return changedValues
}

// Guard for values used directly in an <img src>. Allows only image data URIs,
// blob URLs, http(s) URLs and relative paths — blocks unsafe schemes (DOMXSS).
export const sanitizeImageSrc = (value?: string) =>
  value && /^(blob:|https?:\/\/|\/|data:image\/)/i.test(value) ? value : ''

export const formatFileSize = (size: any) => {
  if (size) {
    const KB = size / 1024
    const MB = KB / 1024
    if (MB >= 1) {
      return `${MB.toFixed(2)} Mb`
    } else if (KB >= 1) {
      return `${KB.toFixed(2)} Kb`
    } else {
      return `${size} bytes`
    }
  }
}

export const isPdfSizeExceedsLimit = (fileSizeInBytes: number | undefined) => {
  const maxSizeInBytes = 30 * 1024 * 1024 // 30 MB in bytes

  return fileSizeInBytes ? fileSizeInBytes > maxSizeInBytes : false
}

export const handleGetDates = (interval: string) => {
  {
    const today = dayjs() // Get current date

    switch (interval) {
      case 'day':
        return { startDate: today.startOf('day'), endDate: today.endOf('day') }
      case 'week':
        return {
          startDate: today.startOf('week'),
          endDate: today.endOf('week')
        }
      case 'month':
        return {
          startDate: today.startOf('month'),
          endDate: today.endOf('month')
        }
      case 'three_month':
        return {
          startDate: today.subtract(3, 'month').startOf('month'),
          endDate: today.endOf('month')
        }
      case 'six_month':
        return {
          startDate: today.subtract(6, 'month').startOf('month'),
          endDate: today.endOf('month')
        }
      case 'year':
        return {
          startDate: today.startOf('year'),
          endDate: today.endOf('year')
        }
      case 'three_year':
        return {
          startDate: today.subtract(3, 'year').startOf('year'),
          endDate: today.endOf('year')
        }
      case 'past_7_days':
        return {
          startDate: today.subtract(6, 'day').startOf('day'),
          endDate: today.endOf('day')
        }
      case 'past_30_days':
        return {
          startDate: today.subtract(29, 'day').startOf('day'),
          endDate: today.endOf('day')
        }
      case 'past_90_days':
        return {
          startDate: today.subtract(89, 'day').startOf('day'),
          endDate: today.endOf('day')
        }
      case 'past_year':
        return {
          startDate: today.subtract(1, 'year').startOf('year'),
          endDate: today.subtract(1, 'year').endOf('year')
        }
      default:
        throw new Error('Invalid interval specified.')
    }
  }
}

export interface chartdataTypes {
  data: number[]
  chartlabels: string[]
  colors: string[]
}

export interface pieChartTypes {
  label?: boolean
  chartData: chartdataTypes
  centertext: React.ReactNode
}

export const handleSessionVisits = async (
  page: boolean,
  login: boolean,
  paramVisitData?: any,
  initialVisit?: boolean,
  initialLogin?: boolean
) => {
  try {
    const VisitData = sessionStorage.getItem('visitData')

    let resp
    if (paramVisitData || VisitData) {
      const parsedData = JSON.parse(paramVisitData || VisitData)
      const data = {
        session_id: parsedData.session_id,
        logins: initialLogin ? 1 : login ? parsedData.logins + 1 : parsedData.logins,
        page_visit: initialVisit ? 1 : page ? parsedData.page_visit + 1 : parsedData.page_visit
      }
      sessionStorage.setItem('visitData', JSON.stringify(data))
      // The server reads the visitor address from the connection; the browser
      // only adds whether it is being driven by automation.
      resp = await CommonApi({
        path: 'site-analysis',
        method: 'patch',
        data: { ...data, webdriver: navigator.webdriver === true }
      })
    } else {
      const data = {
        session_id: new Date().getTime().toString(),
        logins: 0,
        page_visit: 1
      }
      sessionStorage.setItem('visitData', JSON.stringify(data))
    }
    return resp
  } catch (error) {
    // Swallow errors to avoid noisy logs; analytics should never block UI
    console.warn('handleSessionVisits failed (non-blocking)')
  }
}

export function createDateObject(dateStr: string, timeStr: string) {
  let dateTime
  let newDateStr = dateStr
  let newTimeStr = timeStr
  if (dateStr?.includes(':')) {
    newDateStr = dayjs(newDateStr).format('DD/MM/YYYY')
  }
  if (dateStr?.startsWith('Today')) {
    newDateStr = dayjs(new Date()).format('DD/MM/YYYY')
  }
  if (timeStr?.startsWith('Now')) {
    newTimeStr = dayjs(new Date()).format('hh:mm:a')
  }
  // Split date and time strings
  const [day, month, year] = newDateStr?.split('/')
  const [time] = newTimeStr?.split(/[ap]m/i)
  const period = newTimeStr?.split(':')[2]
  // Parse time into hours and minutes
  const [hours, minutes] = time.split(':').map(part => parseInt(part, 10))
  // Convert hours to 24-hour format if necessary
  let hours24 = hours

  if (newDateStr && /^\d{2}\/\d{2}\/\d{4}$/.test(newDateStr)) {
    if (period && period.toLowerCase() === 'pm' && hours !== 12) {
      hours24 = hours + 12
    } else if (period && period.toLowerCase() === 'am' && hours === 12) {
      hours24 = 0
    }

    dateTime = new Date(parseInt(year), parseInt(month) - 1, parseInt(day), hours24, minutes)
  }

  dateTime = dayjs(`${year}-${month}-${day} ${hours24}:${minutes}`)
  return dateTime ? (dateTime.isValid() ? dateTime : null) : null
}

const debounce = (func: any, delay: any) => {
  let timeoutId: any
  return (...args: any) => {
    if (timeoutId) {
      clearTimeout(timeoutId)
    }
    timeoutId = setTimeout(() => {
      func(...args)
    }, delay)
  }
}

export default debounce

export const createImage = (url: any) =>
  new Promise((resolve, reject) => {
    const image = new Image()
    image.addEventListener('load', () => resolve(image))
    image.addEventListener('error', error => reject(error))
    image.setAttribute('crossOrigin', 'anonymous') // needed to avoid cross-origin issues on CodeSandbox
    image.src = url
  })

export const getRadianAngle = (degreeValue: any) => (degreeValue * Math.PI) / 180

export const isBase64 = (str: string) => {
  if (typeof str !== 'string') {
    return false
  }

  // Regular expression to test for Base64 format
  const base64Pattern = /^(?:[A-Za-z0-9+/]{4})*?(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/

  // Remove any line breaks or spaces
  const cleanStr = str.trim().replace(/\s/g, '')

  // Test the string against the regex
  return base64Pattern.test(cleanStr)
}

export const formatString = (str: string) => {
  return str?.replace(/[^a-zA-Z0-9,\s]/g, '') // Remove all characters except letters, digits, commas, and spaces
}

export function convertTimestampToDate(timestamp: any) {
  const date = new Date(timestamp * 1000) // Convert from seconds to milliseconds
  return date.toLocaleDateString('en-GB') // Format as "DD/MM/YYYY"
}

export const formatContentHeadline = (text: string) => {
  // Replace '/' with '&#47;'
  text = text.replace(/\//g, '&#47;')

  // Remove all special characters and emojis except '/' (which is now '&#47;')
  return text.replace(/[^\w\s&#47;]/g, '')
}

export const convertLocalToUtc = (dateInput: string, timeInput: string) => {
  const localTimeString = `${dateInput} ${timeInput}`

  // Parse using the known format
  const parsed = dayjs(localTimeString, 'MM/DD/YYYY hh:mm:a')

  if (!parsed.isValid()) {
    throw new Error('Invalid date format')
  }

  return parsed.utc().toISOString()
}

export const getUpdatedValuesadvance = (oldObject: any, newObject: any): any => {
  const changedValues: any = {}

  // Helper function for deep comparison
  const hasChanged = (oldVal: any, newVal: any): boolean => {
    // Check if both values are objects
    if (
      typeof oldVal === 'object' &&
      oldVal !== null &&
      typeof newVal === 'object' &&
      newVal !== null
    ) {
      return !Object.is(oldVal, newVal)
    }
    return !Object.is(oldVal, newVal)
  }

  // Iterate over keys in the new object
  for (const key in newObject) {
    if (oldObject && oldObject.hasOwnProperty(key)) {
      // Check if the value has changed (using deep comparison)
      if (hasChanged(oldObject[key], newObject[key])) {
        // If both values are objects, check for nested changes
        if (
          typeof oldObject[key] === 'object' &&
          oldObject[key] !== null &&
          typeof newObject[key] === 'object' &&
          newObject[key] !== null
        ) {
          const nestedChanges = getUpdatedValues(oldObject[key], newObject[key])
          if (Object.keys(nestedChanges).length > 0) {
            changedValues[key] = nestedChanges
          }
        } else {
          changedValues[key] = newObject[key]
        }
      }
    } else {
      // New key, consider it a change
      changedValues[key] = newObject[key]
    }
  }

  return changedValues
}

export function areArraysDifferent(arr1: any, arr2: any) {
  // Create sets from both arrays to remove duplicates and ignore order
  const set1: any = new Set(arr1)
  const set2: any = new Set(arr2)

  // Check if there are values in set1 that are not in set2
  for (const value of set1) {
    if (!set2.has(value)) {
      return true // Found a difference
    }
  }

  // Check if there are values in set2 that are not in set1
  for (const value of set2) {
    if (!set1.has(value)) {
      return true // Found a difference
    }
  }

  return false // No differences found
}

export const ControlOpacity = (hex: string, opacity: number = 1) => {
  // Remove the '#' if present
  hex = hex.replace(/^#/, '')

  // If shorthand hex (e.g., #03F), expand to full form (e.g., #0033FF)
  if (hex.length === 3) {
    hex = hex
      .split('')
      .map(char => char + char)
      .join('')
  }

  // Parse the r, g, b values
  const r = parseInt(hex.slice(0, 2), 16)
  const g = parseInt(hex.slice(2, 4), 16)
  const b = parseInt(hex.slice(4, 6), 16)

  // Return the rgba color string
  return `rgba(${r}, ${g}, ${b}, ${opacity})`
}

export function removeDuplicateObjects<T extends DuplicateItem>(array: T[], key: keyof T): T[] {
  const seen = new Set()
  return array.filter(item => {
    const value = item[key]
    if (seen.has(value)) {
      return false
    }
    seen.add(value)
    return true
  })
}

export const formatPrice = (price?: number) => {
  if (price === undefined || price === null) return ''

  // Convert to string to check decimal places
  const priceStr = price.toString()
  const decimalPart = priceStr.split('.')[1]

  if (decimalPart && decimalPart.length > 2) {
    return price.toFixed(3)
  } else {
    return price.toFixed(2)
  }
}

export const getAnnouncementButtonText = ({
  type,
  category
}: {
  type?: string | string[] | null
  category?: string | string[] | null
}): string => {
  const contentType = Array.isArray(type) ? type[0] : type
  const contentCategory = Array.isArray(category) ? category[0] : category

  if (contentType === 'youtube' || contentType === 'video') return 'Watch Video'
  if (contentCategory === 'update') return 'Go to News & Insights'
  return `Go to ${contentCategory ?? ''}`
}

export const isValidUrl = (url: string): boolean =>
  /^((https?:\/\/)?([\w-]+\.)+[\w-]{2,})(\/\S*)?$/i.test(url)

export const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)

export const isYouTubeUrl = (url: string): boolean => !!getYouTubeVideoId(url)
export const isVimeoUrl = (url: string): boolean => !!getVimeoVideoId(url)

export const isValidVideoUrl = (url: string): boolean => isYouTubeUrl(url) || isVimeoUrl(url)

export const isZoomUrl = (url: string | undefined | null): boolean => {
  if (!url) return false

  try {
    const { hostname } = new URL(/^https?:\/\//i.test(url) ? url : `https://${url}`)
    return /(^|\.)(zoom\.us|zoom\.com|zoomgov\.com)$/i.test(hostname)
  } catch {
    return false
  }
}

export const getWebLinkButtonText = (url: string | undefined | null): string =>
  isZoomUrl(url) ? 'Watch video' : 'Read article'

export const getYouTubeVideoId = (url: string | undefined | null): string | null => {
  const regex =
    /(?:youtube\.com\/(?:watch\?(?:[^#]*&)?v=|embed\/|shorts\/|live\/|v\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/
  const match = url?.match(regex)
  return match ? match[1] : null
}

export const getYouTubeThumbnail = (url: string | undefined | null): string | null => {
  const videoId = getYouTubeVideoId(url)
  if (!videoId) return null
  return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`
}

export const getVimeoVideoId = (url: string | undefined | null): string | null => {
  if (!url) return null

  // supports:
  // https://vimeo.com/123456789
  // https://player.vimeo.com/video/123456789
  // https://vimeo.com/channels/staffpicks/123456789
  const regex = /(?:vimeo\.com\/(?:video\/|channels\/[\w]+\/|groups\/[^/]+\/videos\/)?)(\d+)/

  const match = url.match(regex)
  return match ? match[1] : null
}

export const getYoutubeEmbedUrl = (
  url: string | undefined | null,
  params: URLSearchParams | null = null,
  autoplay: boolean = false // <-- new flag
): string => {
  if (!url) return ''

  const finalParams = params ? new URLSearchParams(params.toString()) : new URLSearchParams()

  if (autoplay) {
    finalParams.set('autoplay', '1')
  }

  const ytId = getYouTubeVideoId(url)
  if (ytId) {
    return `https://www.youtube-nocookie.com/embed/${ytId}?${finalParams.toString()}`
  }

  const vimeoId = getVimeoVideoId(url)
  if (vimeoId) {
    return `https://player.vimeo.com/video/${vimeoId}?${finalParams.toString()}`
  }

  return ''
}

export const convertLexicalToHTML = (lexicalJson: string): string => {
  try {
    const parsed = JSON.parse(lexicalJson)
    const root = parsed.root

    if (!root || !root.children) {
      return ''
    }

    let html = ''

    root.children.forEach((node: any) => {
      if (node.type === 'paragraph') {
        const textContent = node.children
          .map((child: any) => {
            if (child.type === 'merge_tag' || child.type === 'text') {
              return child.text || ''
            }
            return ''
          })
          .join('')

        html += `<p style="line-height: 140%;">${textContent}</p>`
      }
    })

    return html
  } catch (error) {
    console.error('Error converting Lexical to HTML:', error)
    return ''
  }
}

export const getAnnouncementUrl = (
  headline?: string | null,
  id?: string | number | null,
  opts?: {
    is_custom_site?: boolean | null
    custom_site_announcements_url?: string | null
    category?: string | null
    relait_essentials?: boolean | null
    document_url?: string | null
  }
) => {
  const documentUrl = (opts?.document_url || '').trim()
  if (opts?.relait_essentials && /^https?:\/\//i.test(documentUrl)) {
    return documentUrl
  }

  if (!headline || id === undefined || id === null) return '#'

  const encodedHeadline = encodeURIComponent(headline)
  const b64 = btoa(JSON.stringify(id))
  const canonical = (opts?.custom_site_announcements_url || '').trim().replace(/\/$/, '')
  const isAnnouncement = !opts?.category || opts.category.toLowerCase() === 'announcement'

  if (opts?.is_custom_site && canonical && isAnnouncement) {
    const sep = canonical.includes('?') ? '&' : '?'
    return `${canonical}${sep}announcement=${encodeURIComponent(b64)}`
  }

  return `/announcement-detail/${encodedHeadline}-${b64}`
}

interface SocialPostRelatedContent {
  id?: string | number | null
  headline?: string | null
  category?: string | null
}

export const getSocialPostMediaUrl = (
  relatedContent?: SocialPostRelatedContent | null,
  mediaUrl?: string | null,
  opts?: {
    is_custom_site?: boolean | null
    custom_site_announcements_url?: string | null
  }
) => {
  if (relatedContent?.id != null && relatedContent?.headline) {
    return getAnnouncementUrl(relatedContent.headline, relatedContent.id, {
      is_custom_site: opts?.is_custom_site,
      custom_site_announcements_url: opts?.custom_site_announcements_url,
      category: relatedContent.category
    })
  }

  return (mediaUrl || '').trim()
}

export const getAnnouncementIdFromSlug = (slug?: string): number | null => {
  if (!slug) return null

  const parts = slug.split('-')
  const raw = parts[parts.length - 1]

  // numeric ID (new if we want to convert to numeric)
  if (/^\d+$/.test(raw)) {
    return Number(raw)
  }

  // base64 ID (historical version)
  try {
    const decoded = Buffer.from(raw, 'base64').toString('utf8')
    const parsed = Number(decoded)
    return Number.isFinite(parsed) ? parsed : null
  } catch {
    return null
  }
}

const DATE_FORMATS = {
  short: 'DD/MM/YYYY HH:mm',
  long: 'D MMM YYYY, HH:mm',
  dateOnly: 'D MMM YYYY',
  slashDate: 'DD/MM/YYYY',
  db: 'YYYY-MM-DD',
  tiny: 'DD/MM/YY'
}

export const formatDate = (
  date: string | null | undefined | Dayjs | Date,
  formatType: keyof typeof DATE_FORMATS = 'short',
  defaultResponse = '',
  applyLocal = true
): string => {
  if (!date) return defaultResponse

  let parsed = dayjs.utc(date)
  if (!parsed.isValid()) return defaultResponse

  if (applyLocal) parsed = parsed.local()

  return parsed.format(DATE_FORMATS[formatType])
}

export const parseApiError = (error: any): string => {
  // Network error (no internet / server down)
  if (!error?.response) {
    return 'Network error. Please check your internet connection.'
  }

  const data = error.response.data

  // Normalize all possible backend formats
  const message = data?.msg || data?.message || data?.error || data?.detail || data?.title || ''
  const text = Array.isArray(message) ? message[0]?.msg : message

  return typeof text === 'string' ? text : 'Something went wrong. Please try again.'
}

export const parseApiSuccess = (msg: unknown): string => {
  if (msg === null || msg === undefined) return ''

  // If array -> join
  if (Array.isArray(msg)) {
    return msg.join(', ')
  }

  // If object -> stringify meaningful values
  if (typeof msg === 'object') {
    try {
      return Object.values(msg).flat().join(', ')
    } catch {
      return 'Operation completed successfully.'
    }
  }

  // number / boolean / string
  return String(msg)
}

/**
 * LinkedIn escapes its reserved characters with a backslash, and Ayrshare hands the text back
 * to us that way — "Tungsten Mining \(ASX:TGN\)". The escaping is not limited to brackets, so
 * rather than enumerate the set we drop a backslash before anything that is not a letter,
 * digit or whitespace. That leaves real newlines and content like "50\50" untouched.
 *
 * Run this before any hashtag/mention/link formatting: "\#Foo" would otherwise match the
 * hashtag pattern and leave the stray backslash sitting outside the rendered tag.
 */
export const unescapeSocialText = (text?: string | null): string => {
  if (!text) return ''
  return text.replace(/\\([^A-Za-z0-9\s])/g, '$1')
}

/**
 * Post bodies come back from the social platforms via the sync, so they are not trusted input.
 * Anywhere the text is injected with dangerouslySetInnerHTML the author's own characters must be
 * neutralised first — only the tags we build ourselves may render.
 */
export const escapeHtml = (text?: string | null): string => {
  if (!text) return ''
  return text
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;')
}
